import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { may, useTenants } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { Verdict } from './verdict'

type Deliverability = Schema<'DeliverabilityResponse'>
type Sender = Schema<'SenderOut'>
type Check = Schema<'DeliverabilityCheckOut'>
type Rates = Schema<'DeliverabilityRatesOut'>
type Sending = Schema<'SendingPauseOut'>
type Summary = Schema<'MechanismSummaryOut'>

/**
 * Deliverability — SPF, DKIM and DMARC for every sending identity (S-B31b).
 *
 * The design's reason for putting it beside the funnel rather than on a settings
 * page: deliverability and ban risk *"are the two things that silently
 * destroy an outreach programme"*, and neither announces itself in the numbers
 * above.
 *
 * **Four states, not three, and the fourth is the point.** `pass`, `warn` and
 * `fail` are claims about the domain; `unknown` is a claim about the lookup. The
 * server keeps them apart — a missing DMARC record is a failure, a DNS timeout
 * is not — and this renders them with different colours *and* different words,
 * because two greys would put a tenant back where the distinction started.
 *
 * **The record and the name it was found at are both shown.** A tenant arguing
 * with this panel should be able to run the same `dig` and get the same answer;
 * a verdict with no evidence under it is one they can only accept or ignore.
 *
 * **G15 added a second half, and it answers a different question.** The DNS
 * checks are what the tenant's domain *promises*; the rates are what the
 * receiving world actually *did* with the mail. A domain can pass all three
 * checks and still bounce 8% of its messages at a bought list — which is exactly
 * the failure the first half cannot see, and the one that costs a sending domain.
 *
 * **The rates card comes first when sending has been stopped**, because at that
 * moment nothing else on the screen matters: the tenant's outreach is not going
 * out and the only useful thing the page can do is say so and offer the way back.
 */
export function DeliverabilityPanel(): JSX.Element {
  const senders = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/deliverability', { signal })),
    [],
  )

  // Parity P-ODASH-06 (2026-09-13): `outreach.html:164-186` draws one card
  // headed *Deliverability* — badge, three meters, the SPF/DKIM/DMARC row. That
  // card is the rates card; the per-address cards (the detail a broken record is
  // fixed from) follow it in the same stack, as app extras.
  return (
    <div className="ar-stack" id="deliverability">
      {senders.state.status === 'loading' ? (
        <div className="ar-card">
          <div className="ar-card__header">
            <h2>Deliverability</h2>
          </div>
          <div className="ar-card__body">
            <Loading label="Checking your sending domains" lines={3} />
          </div>
        </div>
      ) : null}

      {senders.state.status === 'error' ? (
        <div className="ar-card">
          <div className="ar-card__header">
            <h2>Deliverability</h2>
          </div>
          <ErrorState
            failure={senders.state.failure}
            title="Could not check your sending domains"
            onRetry={senders.refetch}
          />
        </div>
      ) : null}

      {senders.state.status === 'ready' ? <Ready data={senders.state.data} /> : null}
    </div>
  )
}

function Ready({ data }: { data: Deliverability }): JSX.Element {
  if (data.empty) {
    return (
      <>
        {/* **The stop is shown even with no sending identity configured.** The two
            halves are independent — a tenant can have messages out on another
            channel, and an automatic stop that was invisible because a DNS card
            had nothing to draw would be the worst possible place to hide it. */}
        {data.sending.paused ? (
          <RatesCard rates={data.rates} sending={data.sending} summary={data.summary} />
        ) : null}
        <div className="ar-card">
          <div className="ar-card__header">
            <h2>Deliverability</h2>
          </div>
          <Empty
            icon="@"
            title="No sending address set up"
            body="Outreach email goes out from an address on your own domain, and three DNS records decide whether it arrives. Once an address is set up, this checks all three against live DNS every time you open the page."
            action={
              <Link className="ar-btn ar-btn--primary" to="/settings/connections">
                Set up a sender
              </Link>
            }
          />
        </div>
      </>
    )
  }
  return (
    <>
      <RatesCard rates={data.rates} sending={data.sending} summary={data.summary} />
      <div className="ar-stack">
        {data.senders.map((sender) => (
          <SenderCard key={sender.address} sender={sender} />
        ))}
      </div>
      <p className="ar-subtle app-deliver__caption">
        Read from live DNS at {formatDate(data.checked_at)}. A record you have just added can take a
        few minutes to reach the resolvers that answer for you.
        {data.truncated_to === null
          ? ''
          : ` Showing the first ${String(data.truncated_to)} sending addresses.`}
      </p>
    </>
  )
}

/**
 * What the receiving world did with the mail, and the stop that follows from it.
 *
 * **Three meters and one sentence**, which is `design/screens/outreach.html#dash`
 * exactly: *Delivered*, *Bounced*, *Marked as spam*, and under the bounce bar the
 * rule that acts on it.
 *
 * **`null` is rendered as a dash, never as 0.0%.** A tenant who has sent nothing
 * has no bounce rate, and a panel showing `0.0%` for them says *your list is
 * clean* about a list nobody has tested — the exact lie this row of the audit was
 * opened for. The server sends `null` and the three states it distinguishes
 * (`too_early`, `healthy`, `pause`) each get their own sentence.
 *
 * **The threshold comes from the server**, not from a `5` written here. It is the
 * same argument `AccountHealthPanel` makes about the LinkedIn caps: the number
 * that fires the rule and the number on the screen must be one number.
 */
function RatesCard({
  rates,
  sending,
  summary,
}: {
  rates: Rates
  sending: Sending
  summary: Summary[]
}): JSX.Element {
  return (
    <article className="ar-card">
      <div className="ar-card__header">
        <h2>Deliverability</h2>
        <span className="ar-card__spacer" />
        <VerdictBadge verdict={rates.verdict} paused={sending.paused} />
      </div>
      <div className="ar-card__body ar-stack">
        {sending.paused ? (
          <div className="app-deliver__stop" role="alert">
            <p>{sending.reason ?? 'Sending is stopped for this organisation.'}</p>
            <ResumeButton />
          </div>
        ) : null}

        <RateMeter label="Delivered" percent={rates.delivered_percent} tone="good" />
        <RateMeter
          label="Bounced"
          percent={rates.bounce_percent}
          tone={overLimit(rates) ? 'bad' : 'good'}
          note={bounceNote(rates)}
        />
        <RateMeter label="Marked as spam" percent={rates.complaint_percent} tone="good" />

        <p className="ar-subtle app-deliver__caption">
          {rates.resolved === 0
            ? `Nothing has come back yet. Figures appear once messages sent in the last ${String(rates.window_days)} days are delivered or bounced.`
            : `${String(rates.resolved)} messages in the last ${String(rates.window_days)} days have a final result.`}
        </p>

        {summary.length === 0 ? null : (
          <>
            <hr className="app-deliver__rule" />
            <DnsStrip summary={summary} />
          </>
        )}
      </div>
    </article>
  )
}

/**
 * SPF · DKIM · DMARC as three badges in one row — OUTR-5 and OUTR-43.
 *
 * **The per-sender cards below are not replaced by this and were never the
 * problem.** The mockup has one sending address and can afford to draw each
 * mechanism once, with its verdict, its record and the name it was found at; this
 * product has N, and the detail is what a tenant fixes a broken record from. What
 * the app had no answer for is the question the mockup's compact row answers in
 * one glance — *are my three records right?* — which on a page of four sender
 * cards and twelve check rows takes a while to work out.
 *
 * **The fold is the server's** (`deliverability.summarise`), and putting it there
 * rather than here is the point of this component being four lines. The ordering
 * it folds on is `SEVERITY`, which `overall` already uses for the per-sender
 * badge; a second copy of *fail beats warn beats unknown beats pass* in
 * TypeScript would have been free to disagree with it, and the disagreement's
 * shape is a green tick over an address with no DMARC record.
 *
 * **It also stops the strip lying when the sender list is truncated.** Only the
 * first `MAX_SENDERS` addresses are resolved at all, so past that bound nothing
 * knows the worst status — the server sends no summary and this draws nothing.
 * That decision needs `truncated_to`, which is why it cannot sensibly be made
 * here from a list that has already been cut.
 *
 * **The strip is a summary of what is below it, so it is `aria-hidden`.** Every
 * badge here repeats a `CheckRow` in the same panel, with less detail; a screen
 * reader that read both would hear each mechanism twice and the second, fuller
 * reading is the useful one. The visual jump-to-detail this offers a sighted
 * reader is exactly what a screen reader does not need.
 */
function DnsStrip({ summary }: { summary: Summary[] }): JSX.Element {
  return (
    <div className="ar-row app-deliver__dns" aria-hidden="true">
      {summary.map((entry) => (
        <Verdict key={entry.mechanism} tone={tone(entry.status)}>
          {entry.text}
        </Verdict>
      ))}
    </div>
  )
}

/** Whether the bounce rate is over the line the server reports. */
function overLimit(rates: Rates): boolean {
  return rates.bounce_percent !== null && rates.bounce_percent > rates.pause_above_percent
}

/**
 * The sentence under the bounce bar — the mockup's *"above 5% we pause sending
 * automatically"*, with the tenant's own position said first.
 *
 * Three sentences for three states, because *"we have not seen enough to say"*
 * and *"you are fine"* must not read the same.
 */
function bounceNote(rates: Rates): string {
  const limit = `above ${String(rates.pause_above_percent)}% we pause sending automatically`
  if (rates.verdict === 'too_early') {
    return `Not enough messages have come back to judge yet — ${limit}.`
  }
  if (rates.bounce_percent === null) return `Nothing measured yet — ${limit}.`
  if (overLimit(rates)) {
    return `Over the limit — ${limit}.`
  }
  return `Under ${String(rates.pause_above_percent)}% — ${limit}.`
}

/** One labelled bar. A `null` rate renders a dash and an empty track. */
function RateMeter({
  label,
  percent,
  tone,
  note,
}: {
  label: string
  percent: number | null
  tone: 'good' | 'bad'
  note?: string
}): JSX.Element {
  const width = percent === null ? 0 : Math.min(100, Math.max(0, percent))
  return (
    <div>
      <div className="ar-row app-deliver__rate-head">
        <span>{label}</span>
        <span className="ar-card__spacer" />
        <span className="ar-mono">{percent === null ? '—' : `${percent.toFixed(1)}%`}</span>
      </div>
      <div
        className={`ar-meter ${tone === 'bad' ? 'ar-meter--over' : ''}`}
        role="meter"
        // The figure is read out, because a bar carries nothing to a reader who
        // cannot see it — the same rule `AccountHealthPanel`'s meters follow.
        aria-label={
          percent === null ? `${label}: not measured yet` : `${label}: ${String(percent)}%`
        }
        aria-valuenow={percent ?? 0}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="ar-meter__fill" style={{ width: `${String(width)}%` }} />
      </div>
      {note === undefined ? null : <div className="ar-subtle app-deliver__rate-note">{note}</div>}
    </div>
  )
}

/** The badge beside the heading. Says which of the three states this is. */
function VerdictBadge({ verdict, paused }: { verdict: string; paused: boolean }): JSX.Element {
  if (paused) return <Verdict tone="danger">Sending stopped</Verdict>
  if (verdict === 'pause') return <Verdict tone="danger">Over the limit</Verdict>
  // `ar-badge--neutral`, not a bare `ar-badge`: `.ar-badge` alone sets no
  // background and no colour (`components.css:281-291`), so *Not enough data*
  // rendered as unstyled text beside two badges that were drawn. Recorded as a
  // suggestion in the parity report and fixed here because the row it sits in is
  // this session's.
  if (verdict === 'too_early') return <Verdict tone="neutral">Not enough data</Verdict>
  return <Verdict tone="success">Healthy</Verdict>
}

/**
 * *Resume sending*, after the tenant has cleaned their list.
 *
 * **The only way the stop is ever cleared.** Nothing lowers it automatically —
 * a 30-day rolling rate falls back under the threshold on its own as bad sends
 * age out, and an organisation that un-paused itself on that would never have
 * the list looked at.
 *
 * Its own component so the button owns its pending and error states rather than
 * pushing them up into a card that is otherwise a read.
 */
function ResumeButton(): JSX.Element {
  const resume = useMutation(() => call(() => api.POST('/api/v1/outreach/sending/resume', {})))
  const { active } = useTenants()
  const toast = useToast()
  if (active === null || !may(active.membership, 'budget:manage')) {
    return (
      <p className="ar-subtle">
        An owner or admin can resume sending once the list has been cleaned.
      </p>
    )
  }
  return (
    <>
      <InlineError failure={resume.state.failure} />
      <Button
        size="sm"
        busy={resume.pending}
        onClick={() => {
          void (async () => {
            const result = await resume.run(undefined)
            if (!result.ok) return
            toast.show({
              kind: 'success',
              title: 'Sending resumed',
              body: 'Outreach will go out again. If the list is still bouncing we will stop it once more.',
            })
            window.location.reload()
          })()
        }}
      >
        Resume sending
      </Button>
    </>
  )
}

function SenderCard({ sender }: { sender: Sender }): JSX.Element {
  return (
    <article className="ar-card">
      <div className="ar-card__header">
        <h3>{sender.address}</h3>
        <span className="ar-card__spacer" />
        {sender.connection_status === 'connected' ? null : (
          // The records being right is necessary and not sufficient. A tenant
          // with three green ticks and a sender that never finished connecting
          // would otherwise conclude they are sending.
          <span className="ar-badge ar-badge--neutral">Not sending yet</span>
        )}
        <Verdict tone={tone(sender.status)}>{headline(sender.status)}</Verdict>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {sender.checks.map((check) => (
          <CheckRow key={check.mechanism} check={check} />
        ))}
      </div>
    </article>
  )
}

function CheckRow({ check }: { check: Check }): JSX.Element {
  return (
    <div className="app-deliver__check">
      <span className={`ar-badge ar-badge--${tone(check.status)} app-deliver__mech`}>
        {check.label}
      </span>
      <div className="app-deliver__body">
        <p className="app-deliver__detail">{check.detail}</p>
        {check.name === '' ? null : (
          <p className="ar-subtle app-deliver__evidence">
            <span className="ar-mono">{check.name}</span>
            {check.record === null ? null : (
              <>
                {' — '}
                <span className="ar-mono app-deliver__record">{check.record}</span>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Status to badge colour.
 *
 * `unknown` is neutral rather than a fourth colour, because it is genuinely the
 * absence of a verdict — colouring it would imply a judgement the server
 * explicitly declined to make.
 */
const TONE: Record<string, string> = {
  pass: 'success',
  warn: 'warning',
  fail: 'danger',
  unknown: 'neutral',
}

/** What a whole sender's badge says. The words carry the meaning; colour repeats it. */
const HEADLINE: Record<string, string> = {
  pass: 'Healthy',
  warn: 'Needs attention',
  fail: 'Not protected',
  unknown: 'Could not check',
}

/**
 * Both lookups fall back rather than rendering `undefined` into a class name.
 *
 * `status` arrives as a string, so a value added to `deliverability.Status` in a
 * later release reaches this screen before this file knows about it. Neutral and
 * *Checked* are the honest defaults for a verdict we cannot colour: an unstyled
 * badge and the literal word `undefined` are the two alternatives, and both read
 * as a broken screen rather than as a new state.
 */
function tone(status: string): string {
  return TONE[status] ?? 'neutral'
}

function headline(status: string): string {
  return HEADLINE[status] ?? 'Checked'
}
